/** @format */

import {ReactElement} from 'react';

import {Box} from '@mui/material';
import {Trans} from 'react-i18next';

import i18n from 'i18next';

import {buildFormatterObject, getValueWithCurrency} from 'helpers/numberHelpers';

import {HintIconTooltip} from 'pages/Home/Main/Project/components/HintIconTooltip';

import {Delta} from '../Delta';
import {TooltipContent} from './components/TooltipContent';
import WidgetIsNotAvailable from './components/WidgetIsNotAvailable';
import {usePrediction} from './hooks/usePrediction';
import {PredictionWidgetProps} from './interfaces';
import {
  CenterLineWrap,
  Currency,
  Dot,
  DotBox,
  LabelFrom,
  LabelTo,
  LeftCol,
  MainContent,
  PredictionRow,
  PredictionValue,
  RightCol,
  TopText,
  VerticalLine,
  WidgetTooltip,
} from './styles';
import {getGradientByDeviation, getLabelWithColor} from './utils';
import {WidgetContainer} from '../../styles';

const PredictionWidget = ({tooltipContent, tokenStatus}: PredictionWidgetProps): ReactElement => {
  const {data, loading, error} = usePrediction();

  if (loading || error || !data || tokenStatus === 'NOT_READY') {
    return <WidgetIsNotAvailable data={data} tooltipContent={tooltipContent} />;
  }

  const {format: formatCurrency} = buildFormatterObject(data.currency, i18n.language);

  const {value, currency} = formatCurrency(data.prediction);

  const {label, color} = getLabelWithColor(data.isOverspent ? 'overspent' : 'underspent');
  const labelTrans = (
    <>
      <Trans>{label}</Trans>:
    </>
  );
  const gradient = getGradientByDeviation(
    data.percentageDeviation || 0,
    data.isOverspent ? 'over' : 'under',
  );

  return (
    <WidgetContainer isCenterContent>
      <WidgetTooltip data-testid='project-prediction'>
        <HintIconTooltip title={tooltipContent || <TooltipContent />} placement='top-end' />
      </WidgetTooltip>
      <MainContent>
        <LeftCol>
          <PredictionValue
            sx={{
              fontSize: 16,
              fontWeight: 400,
              textAlign: 'right',
              lineHeight: 1.1,
            }}
          >
            <Trans>Прогноз на</Trans>
            <br />
            {data.targetDateFormatted}
          </PredictionValue>
        </LeftCol>

        <CenterLineWrap>
          <LabelFrom>
            <Trans>от</Trans>
          </LabelFrom>
          <VerticalLine gradient={gradient} />
          <LabelTo>
            <Trans>до</Trans>
          </LabelTo>
        </CenterLineWrap>

        <RightCol data-testid='project-prediction'>
          <TopText>{getValueWithCurrency(data.planTo, currency)}</TopText>

          <DotBox>
            <Dot />
            <Dot />
            <Dot />
          </DotBox>

          <PredictionRow>
            <Box style={{display: 'flex', alignItems: 'center'}}>
              <PredictionValue>{value}</PredictionValue>
              <Currency>{currency}</Currency>
            </Box>
            {data.difference !== 0 && (
              <Delta
                label={labelTrans}
                color={color}
                value={getValueWithCurrency(Math.abs(data.difference), currency)}
              />
            )}
          </PredictionRow>

          <DotBox>
            <Dot />
            <Dot />
            <Dot />
          </DotBox>

          <TopText>{getValueWithCurrency(data.planFrom, currency)}</TopText>
        </RightCol>
      </MainContent>
    </WidgetContainer>
  );
};

export default PredictionWidget;
